import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Paper, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import ENV from "../../../config/env";
import { useAuth } from "../../../context/AuthContext";
import FichaSolicitud from "./FichaSolicitud";
import FacturasEditor, { facturaVacia } from "./FacturasEditor";
import { ESTADOS, agregarFacturasSolicitud, borrarFacturaSolicitud, fmtMonto, getSolicitud, mensajeError } from "../services/solicitudChequeService";

// Detalle de una solicitud: importe, datos, facturas registradas y archivos adjuntos.
// Las facturas se pueden agregar después de pedir el cheque, aunque ya esté emitido (llegan solas al comprobante del egreso).
export default function SolicitudDetalleDialog({ id, onClose, onFacturasAgregadas }) {
  const { user } = useAuth();
  const esContabilidad = ["admin", "gerente", "conta"].includes(user?.role);
  const [datos, setDatos] = useState(null);
  const [porBorrar, setPorBorrar] = useState(null); // factura esperando confirmación
  const [error, setError] = useState("");
  const [nuevas, setNuevas] = useState(null); // null = editor cerrado
  const [guardando, setGuardando] = useState(false);
  const [errorNuevas, setErrorNuevas] = useState("");
  const [aviso, setAviso] = useState("");

  const cargar = useCallback(async (activo = () => true) => {
    try {
      const { data } = await getSolicitud(id);
      if (activo()) setDatos(data);
    } catch (e) {
      if (activo()) setError(mensajeError(e, "No se pudo cargar la solicitud."));
    }
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    let vivo = true;
    setDatos(null);
    setError("");
    setNuevas(null);
    setErrorNuevas("");
    setAviso("");
    setPorBorrar(null);
    cargar(() => vivo);
    return () => { vivo = false; };
  }, [id, cargar]);

  const guardarFacturas = async () => {
    if (nuevas.some((f) => !f.nro_factura.trim())) { setErrorNuevas("Cada factura necesita su número."); return; }
    if (nuevas.some((f) => !f.fecha_factura)) { setErrorNuevas("Cada factura necesita su fecha."); return; }
    setGuardando(true);
    setErrorNuevas("");
    try {
      await agregarFacturasSolicitud(id, nuevas);
      setAviso(`${nuevas.length} factura(s) agregada(s).`);
      setNuevas(null);
      await cargar();
      onFacturasAgregadas?.();
    } catch (e) {
      setErrorNuevas(mensajeError(e, "No se pudieron guardar las facturas."));
    } finally {
      setGuardando(false);
    }
  };

  const borrarFactura = async () => {
    setGuardando(true);
    setErrorNuevas("");
    try {
      await borrarFacturaSolicitud(id, porBorrar.id);
      setAviso(`Factura N.º ${porBorrar.nro_factura} borrada.`);
      setPorBorrar(null);
      await cargar();
      onFacturasAgregadas?.();
    } catch (e) {
      setError(mensajeError(e, "No se pudo borrar la factura."));
      setPorBorrar(null);
    } finally {
      setGuardando(false);
    }
  };

  const estado = ESTADOS[datos?.estado];
  const puedeAgregar = !!estado;
  // Igual que el backend: antes de emitir el cheque, y solo las facturas que cargó el usuario (contabilidad/gerencia, cualquiera).
  const puedeBorrar = (f) => ["espera", "autorizado"].includes(datos?.estado) && (esContabilidad || f.usu === user?.username);

  return (
    <Dialog open={!!id} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography component="span" sx={{ fontSize: 18, fontWeight: 800 }}>Solicitud {id}</Typography>
        {estado && <Chip size="small" label={estado.texto} color={estado.color} sx={{ fontWeight: 700 }} />}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5, display: "flex", flexDirection: "column", gap: 1.5, "& > *": { flexShrink: 0 } }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!datos && !error && <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={24} /></Box>}
        {aviso && <Alert severity="success" onClose={() => setAviso("")}>{aviso}</Alert>}
        {datos && (
          <FichaSolicitud
            fecha={String(datos.fecha || "").slice(0, 10)} cambio={datos.cambio} dirigido={datos.nombre} deposito={datos.deposito} ciudad={datos.ciudad} servicio={datos.servicio}
            moneda={datos.moneda === "Dolares" ? "Dólares" : "Bolivianos"} tipoEgreso={datos.tipo_retencion} importeBs={fmtMonto(datos.bolivianos)} importeUsd={fmtMonto(datos.dolares)}
            concepto={datos.concepto} mes={datos.mes} proyecto={datos.proyecto_nombre} observacion={datos.obs}
            extras={[
              { etiqueta: "Cliente", valor: datos.cliente_nombre },
              { etiqueta: "N.º de cheque", valor: datos.nocheque },
              { etiqueta: "Solicitante", valor: datos.usu },
              datos.facturas.length > 0 && {
                etiqueta: "Facturas",
                valor: (
                  <Box sx={{ display: "grid", gap: 0.25, width: "100%" }}>
                    {datos.facturas.map((f) => (
                      <Box key={f.id} sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
                        <span>N.º {f.nro_factura} · {String(f.fecha_factura || "").slice(0, 10)}{f.denominacion ? ` · ${f.denominacion}` : ""} · Bs {fmtMonto(f.importe_total)}</span>
                        {puedeBorrar(f) && (
                          <Tooltip title="Borrar factura">
                            <IconButton size="small" sx={{ p: 0.25 }} aria-label={`Borrar factura ${f.nro_factura}`} disabled={guardando} onClick={() => { setPorBorrar(f); setAviso(""); }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Box>
                ),
              },
              datos.archivos.length > 0 && {
                etiqueta: "Archivos",
                valor: <Box sx={{ display: "grid", gap: 0.25 }}>{datos.archivos.map((a) => <Link key={a.url} href={`${ENV.API_BASE_URL}${a.url}`} target="_blank" rel="noopener noreferrer">{a.nombre}</Link>)}</Box>,
              },
            ]}
          />
        )}
        {porBorrar && (
          <Alert
            severity="warning"
            action={(
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button color="inherit" size="small" onClick={() => setPorBorrar(null)} disabled={guardando} sx={{ textTransform: "none" }}>No</Button>
                <Button color="error" variant="contained" size="small" onClick={borrarFactura} disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>Sí, borrar</Button>
              </Box>
            )}
          >
            ¿Borrar la factura N.º {porBorrar.nro_factura}?
          </Alert>
        )}
        {datos && nuevas && (
          <Paper ref={(el) => { if (el && !el.dataset.visto) { el.dataset.visto = "1"; el.scrollIntoView({ behavior: "smooth", block: "start" }); } }} variant="outlined" sx={{ p: 1.5, display: "grid", gap: 1.25 }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Agregar facturas</Typography>
              <Typography variant="body2" color="text.secondary">
                {datos.estado === "emitido" ? "El cheque ya fue emitido: las facturas se suman a su comprobante de egreso." : "Se suman a las facturas que ya tiene la solicitud."}
              </Typography>
            </Box>
            {errorNuevas && <Alert severity="error" onClose={() => setErrorNuevas("")}>{errorNuevas}</Alert>}
            <FacturasEditor facturas={nuevas} onChange={setNuevas} />
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        {nuevas ? (
          <>
            <Button onClick={() => { setNuevas(null); setErrorNuevas(""); }} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
            <Button variant="contained" onClick={guardarFacturas} disabled={guardando || !nuevas.length} startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null} sx={{ textTransform: "none", fontWeight: 700 }}>
              {nuevas.length > 1 ? `Guardar ${nuevas.length} facturas` : "Guardar factura"}
            </Button>
          </>
        ) : (
          <>
            <Box>
              {datos && puedeAgregar && (
                <Button startIcon={<ReceiptLongIcon />} onClick={() => { setNuevas([facturaVacia()]); setAviso(""); }} sx={{ textTransform: "none", fontWeight: 700 }}>
                  Agregar facturas
                </Button>
              )}
            </Box>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cerrar</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
